import { Link } from "react-router-dom";
import { Calendar, Gauge, Zap, Wind, Timer, ArrowUpRight } from "lucide-react";

export const VehicleCard = ({ vehicle, index = 0 }) => {
  const {
    slug,
    brand,
    model,
    year_display,
    mileage_display,
    power_kw,
    power_pk,
    top_speed,
    acceleration,
    price_formatted,
    image,
    category,
  } = vehicle;

  return (
    <Link
      to={`/aanbod/${slug}`}
      data-testid={`vehicle-card-${slug}`}
      className="group relative flex flex-col bg-ink-900 border border-white/[0.06] hover:border-champagne/30 transition-[border-color,box-shadow] duration-500 hover:shadow-[0_20px_60px_-20px_rgba(212,175,55,0.15)] animate-fade-up"
      style={{ animationDelay: `${(index % 6) * 60}ms` }}
    >
      {/* Image */}
      <div className="relative aspect-[3/2] overflow-hidden bg-ink-850">
        <img
          src={image}
          alt={`${brand} ${model}`}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover transition-transform ease-out group-hover:scale-[1.06]"
          style={{ transitionDuration: "1200ms" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950/40 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase bg-ink-950/70 backdrop-blur-sm border border-white/10 text-zinc-300 font-body">
            {category}
          </span>
        </div>
        <div className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-white/10 bg-ink-950/70 backdrop-blur-sm opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-500">
          <ArrowUpRight size={14} className="text-champagne" />
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-5 p-6 md:p-7">
        {/* Title */}
        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body">
            {brand}
          </p>
          <h3 className="font-display text-2xl md:text-[26px] text-white leading-tight tracking-tight">
            {model}
          </h3>
        </div>

        {/* Specs grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 hairline-t pt-5">
          <Spec
            icon={<Calendar size={12} />}
            label="Bouwjaar"
            value={year_display}
          />
          <Spec
            icon={<Gauge size={12} />}
            label="Kilometerstand"
            value={mileage_display}
          />
          <Spec
            icon={<Zap size={12} />}
            label="Vermogen"
            value={`${power_kw} kW · ${power_pk} PK`}
          />
          <Spec
            icon={<Wind size={12} />}
            label="Topsnelheid"
            value={`${top_speed} km/h`}
          />
          <Spec
            icon={<Timer size={12} />}
            label="0–100 km/h"
            value={`${acceleration.toString().replace(".", ",")} s`}
            span
          />
        </div>

        {/* Price */}
        <div className="flex items-end justify-between hairline-t pt-5">
          <div className="flex flex-col">
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body mb-1">
              Vanaf
            </span>
            <span className="font-display text-3xl md:text-[32px] text-white tracking-tight leading-none">
              {price_formatted}
            </span>
          </div>
        </div>

        {/* CTA */}
        <div
          data-testid={`vehicle-card-cta-${slug}`}
          className="block w-full py-3.5 mt-1 border border-white/15 text-center text-[11px] tracking-[0.3em] uppercase text-white font-body font-semibold transition-colors duration-500 group-hover:border-champagne group-hover:bg-champagne group-hover:text-ink-950"
        >
          Specificaties
        </div>
      </div>
    </Link>
  );
};

const Spec = ({ icon, label, value, span = false }) => (
  <div
    className={`flex items-start gap-2 ${span ? "col-span-2" : ""}`}
  >
    <span className="mt-0.5 text-champagne-muted opacity-80">{icon}</span>
    <div className="flex flex-col leading-tight">
      <span className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-body">
        {label}
      </span>
      <span className="text-[13px] text-zinc-200 font-body font-medium">
        {value}
      </span>
    </div>
  </div>
);

export default VehicleCard;
